import {
  type OrderExportColumnKey,
  ORDER_EXPORT_COLUMN_ORDER,
  ORDER_EXPORT_HEADER_LO,
} from "@/modules/orders/domain/contracts";
import { Button, Checkbox, Modal } from "@devhop/ui";
import { XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type OrdersExportModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (columns: OrderExportColumnKey[]) => void | Promise<void>;
  confirmLoading?: boolean;
};

export function OrdersExportModal({
  open,
  onOpenChange,
  onConfirm,
  confirmLoading = false,
}: OrdersExportModalProps) {
  const allSet = useMemo(
    () => new Set<OrderExportColumnKey>(ORDER_EXPORT_COLUMN_ORDER),
    [],
  );
  const [selected, setSelected] = useState<Set<OrderExportColumnKey>>(
    () => new Set(allSet),
  );

  useEffect(() => {
    if (open) setSelected(new Set(allSet));
  }, [open, allSet]);

  const toggle = (key: OrderExportColumnKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size <= 1) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleExport = () => {
    const cols = ORDER_EXPORT_COLUMN_ORDER.filter((c) => selected.has(c));
    if (cols.length === 0) return;
    void onConfirm(cols);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} showCloseButton={false}>
      <div className="space-y-4">
        <div className="flex items-start justify-between border-b pb-3">
          <h3 className="font-semibold text-lg">ສົ່ງອອກ CSV</h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="ປິດ"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="text-muted-foreground text-sm">
          ເລືອກຄໍລຳທີ່ຈະລວມໃນໄຟລ໌. ອໍເດີ້ຫຼາຍບຣັດຈະຂຶ້ນຫຼາຍແຖວ; ເລກອໍເດີ້/ທີ່ຢູ່/ເບີ/ຊື່ສະແດງແຖວທໍາອິດຂອງອໍເດີ້ນັ້ນເທົ່ານັ້ນ ສ່ວນ ຍອດ COD/ໂອນແລ້ວ ແບ່ງຕາມບັນທັດເປັນຕົວເລກ.
        </p>
        <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
          {ORDER_EXPORT_COLUMN_ORDER.map((key) => (
            <label
              key={key}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <Checkbox
                checked={selected.has(key)}
                onCheckedChange={() => toggle(key)}
              />
              <span>{ORDER_EXPORT_HEADER_LO[key]}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={confirmLoading}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            onClick={handleExport}
            disabled={confirmLoading || selected.size === 0}
          >
            {confirmLoading ? "ກຳລັງສົ່ງອອກ..." : "ດາວໂຫຼດ"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
